// DiagLog.js
// 実機（特にiOS）でデータが読み込まれない現象を調べるための軽量ログバッファ。
// console を PC に繋がなくても、画面上のボタンからテキストとして取り出せるようにする。

const MAX_ENTRIES = 400;
const MAX_TEXT_LENGTH = 400;

// console.log のうちここに拾うもの。アプリの通常ログは大量なので絞る。
const LOG_PATTERNS = [/^\[items\]/, /^\[project\]/, /^\[map\]/, /^\[diag\]/];

// 診断ログは ?debug=1 が付いているときだけ動かす。
// 通常利用では console のフックもボタン表示も行わない。
export function isDiagEnabled() {
	try {
		return new URLSearchParams(window.location.search).get('debug') === '1';
	} catch (e) {
		return false;
	}
}

const entries = [];
let errorCount = 0;
let installed = false;
let startedAt = null;
// 画面側（MapManager）が状態を提供するためのフック
let contextProvider = null;
// 進行中の非同期処理（ログを早く取っても「今どこで待っているか」が分かるようにする）
const pending = new Map();
let pendingSeq = 0;

// Firebase Storage のダウンロードURLは ?alt=media&token=... の token がアクセス権そのもの。
// ログを他人に渡す前提なので、資格情報になり得るものは記録時点で伏せる。
const REDACT_RULES = [
	[/([?&](?:token|access_token|accessToken|key|signature|sig|Signature|Policy|X-Goog-Signature)=)[^&\s"']+/gi, '$1<redacted>'],
	[/(Bearer\s+)[A-Za-z0-9._~+/-]{10,}/gi, '$1<redacted>'],
	[/("?(?:idToken|refreshToken|accessToken|apiKey|stsTokenManager)"?\s*[:=]\s*"?)[A-Za-z0-9._~+/-]{16,}/gi, '$1<redacted>'],
];

function redact(text) {
	let out = text;
	for (const [re, rep] of REDACT_RULES) out = out.replace(re, rep);
	return out;
}

function nowLabel() {
	const d = new Date();
	const p = (n, w = 2) => String(n).padStart(w, '0');
	return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`;
}

function stringify(value) {
	if (value === null) return 'null';
	if (value === undefined) return 'undefined';
	if (typeof value === 'string') return value;
	if (value instanceof Error) {
		const code = value.code ? ` (code=${value.code})` : '';
		return `${value.name}: ${value.message}${code}`;
	}
	if (typeof value === 'object') {
		// FirebaseError など code を持つものは拾う
		if (value.code || value.message) return `${value.code || ''} ${value.message || ''}`.trim();
		try {
			return JSON.stringify(value);
		} catch (e) {
			return String(value);
		}
	}
	return String(value);
}

function push(level, text) {
	text = redact(text);
	if (text.length > MAX_TEXT_LENGTH) text = text.slice(0, MAX_TEXT_LENGTH) + '…';
	entries.push({ t: nowLabel(), level, text });
	if (level === 'ERR') errorCount++;
	// 古いものから捨てる。ただしエラー件数のカウントは減らさない
	while (entries.length > MAX_ENTRIES) entries.shift();
}

function formatArgs(args) {
	return Array.prototype.map.call(args, stringify).join(' ');
}

export const DiagLog = {
	// index.js から一度だけ呼ぶ。?debug=1 のときのみ有効化する。
	install() {
		if (installed) return;
		if (!isDiagEnabled()) return;
		installed = true;
		startedAt = new Date();

		const origLog = console.log.bind(console);
		const origWarn = console.warn.bind(console);
		const origError = console.error.bind(console);

		console.log = function (...args) {
			try {
				const text = formatArgs(args);
				if (LOG_PATTERNS.some((re) => re.test(text))) push('LOG', text);
			} catch (e) { /* ログ収集で本体を壊さない */ }
			origLog(...args);
		};
		console.warn = function (...args) {
			try { push('WARN', formatArgs(args)); } catch (e) { }
			origWarn(...args);
		};
		console.error = function (...args) {
			try { push('ERR', formatArgs(args)); } catch (e) { }
			origError(...args);
		};

		window.addEventListener('error', (e) => {
			try { push('ERR', `window.onerror: ${e.message} @${e.filename}:${e.lineno}`); } catch (err) { }
		});
		window.addEventListener('unhandledrejection', (e) => {
			try { push('ERR', `unhandledrejection: ${stringify(e.reason)}`); } catch (err) { }
		});
		window.addEventListener('online', () => DiagLog.add('[diag] network online'));
		window.addEventListener('offline', () => DiagLog.add('[diag] network offline'));
		document.addEventListener('visibilitychange', () => DiagLog.add(`[diag] visibility=${document.visibilityState}`));

		DiagLog.add('[diag] DiagLog installed');
	},

	// 明示的にマイルストーンを記録する（読み込みのどこで止まったか特定するため）
	add(...args) {
		if (!installed) return;
		try { push('LOG', formatArgs(args)); } catch (e) { }
	},

	// 待機中の処理を登録する。返り値を done() すると外れる。
	// ログを早いタイミングで取っても「何を何秒待っているか」が残るようにするためのもの。
	beginPending(label) {
		if (!installed) return () => {};
		const id = ++pendingSeq;
		pending.set(id, { label, at: Date.now() });
		return () => pending.delete(id);
	},

	// 画面側の状態をヘッダに載せるための登録
	setContextProvider(fn) {
		if (!installed) return;
		contextProvider = fn;
	},

	isEnabled() {
		return installed;
	},

	getErrorCount() {
		return errorCount;
	},

	getEntryCount() {
		return entries.length;
	},

	clear() {
		entries.length = 0;
		errorCount = 0;
		DiagLog.add('[diag] cleared');
	},

	// コピペしてもらう用のテキスト
	getText() {
		const lines = [];
		const nav = window.navigator || {};
		lines.push('===== CollaboMap 診断ログ =====');
		lines.push(`build     : ${process.env.REACT_APP_BUILD || 'unknown (dev)'}`);
		lines.push(`now       : ${new Date().toISOString()}`);
		lines.push(`started   : ${startedAt ? startedAt.toISOString() : '-'}`);
		lines.push(`url       : ${redact(window.location.href)}`);
		lines.push(`ua        : ${nav.userAgent || '-'}`);
		lines.push(`online    : ${nav.onLine} / visibility: ${document.visibilityState}`);
		lines.push(`screen    : ${window.innerWidth}x${window.innerHeight} dpr=${window.devicePixelRatio}`);
		lines.push(`lang      : ${nav.language || '-'}`);

		if (contextProvider) {
			try {
				const ctx = contextProvider() || {};
				Object.keys(ctx).forEach((k) => {
					lines.push(`${k.padEnd(10)}: ${stringify(ctx[k])}`);
				});
			} catch (e) {
				lines.push(`context   : (取得失敗) ${stringify(e)}`);
			}
		}

		lines.push(`errors    : ${errorCount} / entries: ${entries.length}`);

		// 早くログを取ったときでも、今どこで待っているかが分かるようにする
		if (pending.size > 0) {
			lines.push('----- 待機中 -----');
			const now = Date.now();
			pending.forEach((p) => lines.push(`${((now - p.at) / 1000).toFixed(1)}s 待機中: ${p.label}`));
		}
		else {
			lines.push('待機中の処理: なし');
		}

		lines.push('----- log -----');
		entries.forEach((e) => lines.push(`${e.t} ${e.level.padEnd(4)} ${e.text}`));
		lines.push('===== end =====');
		return lines.join('\n');
	},
};

export default DiagLog;
